import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, Download, Trash2, FileEdit, MessageSquareWarning, Lock,
  CheckCircle2, AlertTriangle, Database, Clock, Mail, KeyRound,
} from "lucide-react";
import { Header } from "@/pages/Vitals";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function Privacy() {
  const { user, login } = useAuth();
  const [summary, setSummary] = useState(null);
  const [consentsData, setConsentsData] = useState(null);
  const [grievances, setGrievances] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, c, g] = await Promise.all([
        api.get("/privacy/me"),
        api.get("/privacy/consents"),
        api.get("/privacy/grievances"),
      ]);
      setSummary(s.data); setConsentsData(c.data); setGrievances(g.data);
    } catch { toast.error("Could not load privacy data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !summary || !consentsData) {
    return <div className="text-[#7D8F84]">Loading privacy center…</div>;
  }

  const totalRecords = Object.values(summary.counts).reduce((a, b) => a + b, 0);
  const activeConsents = Object.values(consentsData.consents).filter(c => c.granted).length;

  return (
    <div className="space-y-6" data-testid="privacy-center">
      <Header
        title="Privacy & Data"
        subtitle="Your data, your choices. Manage consents, view, correct, download or delete your data — and raise a concern with our Data Protection Officer."
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Stat icon={Database} label="Records held" value={totalRecords} color="#4A6750" />
        <Stat icon={ShieldCheck} label="Active consents" value={activeConsents} color="#5B8266" />
        <Stat icon={Clock} label="Policy version" value={`v${summary.policy_version}`} color="#5B7582" />
        <Stat icon={Mail} label="DPO" value={summary.dpo_email} color="#C76B58" small />
      </div>

      {summary.pending_deletion && (
        <Card className="p-4 rounded-2xl border-2 border-[#C76B58] bg-[#C76B58]/5" data-testid="deletion-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#C76B58] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-[#1C2E24]">Account deletion scheduled</p>
              <p className="text-sm text-[#5C6D63] mt-1">
                Your account is scheduled to be permanently deleted on <b>{fmtDate(summary.pending_deletion.scheduled_for)}</b>.
                You can cancel any time before that.
              </p>
              <Button onClick={async () => {
                await api.post("/privacy/delete-account/cancel");
                toast.success("Deletion cancelled"); load();
              }} className="mt-3 rounded-xl bg-[#4A6750] hover:bg-[#3B5340]" data-testid="cancel-deletion-btn">
                Cancel deletion
              </Button>
            </div>
          </div>
        </Card>
      )}

      <ConsentsCard data={consentsData} onChange={load} />

      <CorrectInfoCard user={user} onSaved={async (u) => { login(u); load(); }} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DataExportCard counts={summary.counts} />
        <DeleteAccountCard pending={summary.pending_deletion} onChange={load} />
      </div>

      <GrievanceCard items={grievances} onSubmitted={load} dpoEmail={summary.dpo_email} />

      <RetentionCard policy={summary.retention} />

      {user?.is_admin && <AdminMfaCard />}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color, small }) {
  return (
    <Card className="p-4 rounded-2xl border border-[#E5E7E4]">
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">{label}</p>
          <p className={`font-heading font-semibold leading-tight truncate ${small ? "text-sm" : "text-xl"}`}>{value}</p>
        </div>
      </div>
    </Card>
  );
}

function ConsentsCard({ data, onChange }) {
  const [saving, setSaving] = useState(null);

  const toggle = async (purpose, granted) => {
    setSaving(purpose);
    try {
      if (granted) {
        await api.put("/privacy/consents", { consents: { [purpose]: true } });
        toast.success("Consent granted");
      } else {
        await api.post(`/privacy/consents/withdraw/${purpose}`);
        toast.success("Consent withdrawn");
      }
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update consent");
    } finally { setSaving(null); }
  };

  return (
    <Card className="p-6 rounded-2xl border border-[#E5E7E4]" data-testid="consents-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#5B8266]/15 flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5 text-[#5B8266]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold">My consents</h3>
          <p className="text-sm text-[#5C6D63] mt-1">
            Granted under the Digital Personal Data Protection Act, 2023. Withdraw at any time —
            it is just as easy as granting.
          </p>
        </div>
      </div>

      <ul className="divide-y divide-[#E5E7E4]">
        {Object.entries(data.purposes).map(([key, meta]) => {
          const c = data.consents[key] || {};
          return (
            <li key={key} className="py-4 flex items-start gap-4" data-testid={`consent-${key}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-[#1C2E24]">{meta.label}</p>
                  {meta.required && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#1C2E24]/10 text-[#1C2E24] px-1.5 py-0.5 rounded">
                      Required
                    </span>
                  )}
                  {c.granted ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#5B8266]/15 text-[#5B8266] px-1.5 py-0.5 rounded">
                      <CheckCircle2 className="w-3 h-3" /> Granted
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#7D8F84]/15 text-[#5C6D63] px-1.5 py-0.5 rounded">
                      Not granted
                    </span>
                  )}
                </div>
                <p className="text-sm text-[#5C6D63] mt-1">{meta.description}</p>
                <p className="text-xs text-[#7D8F84] mt-1.5">
                  {c.granted_at && <>Granted {fmtDate(c.granted_at)}</>}
                  {c.withdrawn_at && !c.granted && <>Withdrawn {fmtDate(c.withdrawn_at)}</>}
                  {c.channel && <> · via {c.channel}</>}
                </p>
              </div>
              <Switch
                checked={!!c.granted}
                disabled={meta.required || saving === key}
                onCheckedChange={(v) => toggle(key, v)}
                data-testid={`toggle-${key}`}
              />
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function CorrectInfoCard({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      await api.patch("/privacy/profile", { name: name.trim() });
      const r = await api.get("/auth/me");
      toast.success("Profile updated");
      onSaved(r.data);
    } catch { toast.error("Could not update"); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border border-[#E5E7E4]" data-testid="correct-info-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#5B7582]/15 flex items-center justify-center flex-shrink-0">
          <FileEdit className="w-5 h-5 text-[#5B7582]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold">Correct my information</h3>
          <p className="text-sm text-[#5C6D63] mt-1">Update your registered name. Phone number changes require support.</p>
        </div>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <Label htmlFor="pname">Name</Label>
          <Input id="pname" data-testid="correct-name-input" value={name}
                 onChange={(e) => setName(e.target.value)} className="mt-2 h-11 rounded-xl bg-[#F9F8F6]" />
        </div>
        <Button type="submit" disabled={saving} data-testid="correct-save-btn"
                className="h-11 rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
          {saving ? "Saving…" : "Save"}
        </Button>
      </form>
    </Card>
  );
}

function DataExportCard({ counts }) {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/privacy/export`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `verda-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Data exported");
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border border-[#E5E7E4]" data-testid="export-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#4A6750]/15 flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-[#4A6750]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold">Download my data</h3>
          <p className="text-sm text-[#5C6D63] mt-1">Get a portable JSON copy of everything Verda holds about you.</p>
        </div>
      </div>
      <div className="text-xs text-[#7D8F84] mb-4 space-x-2">
        {Object.entries(counts).map(([k, v]) => <span key={k} className="inline-block">{k}: <b className="text-[#1C2E24]">{v}</b></span>)}
      </div>
      <Button onClick={download} disabled={busy} data-testid="export-btn"
              className="rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">
        <Download className="w-4 h-4 mr-2" /> {busy ? "Preparing…" : "Download (JSON)"}
      </Button>
    </Card>
  );
}

function DeleteAccountCard({ pending, onChange }) {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("delete"); // "delete" | "export_and_forget"

  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "delete") {
        await api.post("/privacy/delete-account", { confirm_phrase: phrase });
        toast.success("Deletion scheduled");
      } else {
        // Export + delete in one call — stream JSON download
        const r = await fetch(`${API}/privacy/export-and-delete`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ confirm_phrase: phrase }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({ detail: "Failed" }));
          throw new Error(err.detail || "Failed");
        }
        const blob = await r.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `verda-export-and-forget-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success("Data exported · account deletion scheduled");
      }
      setOpen(false); setPhrase(""); onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not complete");
    } finally { setBusy(false); }
  };

  const openWith = (m) => { setMode(m); setOpen(true); setPhrase(""); };

  return (
    <Card className="p-6 rounded-2xl border border-[#C76B58]/40 bg-[#C76B58]/[0.03]" data-testid="delete-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#C76B58]/15 flex items-center justify-center flex-shrink-0">
          <Trash2 className="w-5 h-5 text-[#C76B58]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold">Delete my account</h3>
          <p className="text-sm text-[#5C6D63] mt-1">
            Permanently remove your profile and all health data after a 30-day grace period.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button onClick={() => openWith("export_and_forget")} disabled={!!pending} data-testid="export-and-forget-btn"
                className="rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14] disabled:opacity-50 h-auto py-3 whitespace-normal text-left flex flex-col items-start gap-0.5">
          <span className="flex items-center gap-2 text-sm font-semibold">
            <Download className="w-4 h-4" /> Export &amp; forget me
          </span>
          <span className="text-[11px] font-normal text-white/70">Download a copy then schedule deletion — one click.</span>
        </Button>
        <Button onClick={() => openWith("delete")} disabled={!!pending} data-testid="open-delete-btn"
                variant="outline"
                className="rounded-xl border-[#C76B58] text-[#C76B58] hover:bg-[#C76B58]/5 disabled:opacity-50 h-auto py-3 whitespace-normal text-left flex flex-col items-start gap-0.5">
          <span className="flex items-center gap-2 text-sm font-semibold"><Trash2 className="w-4 h-4" /> Delete only</span>
          <span className="text-[11px] font-normal opacity-70">Skip the export. Schedule deletion immediately.</span>
        </Button>
      </div>
      {pending && (
        <p className="mt-3 text-xs text-[#C76B58]">A deletion is already scheduled. Cancel it above before requesting a new one.</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle>{mode === "export_and_forget" ? "Export & forget me?" : "Delete account?"}</DialogTitle>
          </DialogHeader>
          {mode === "export_and_forget" && (
            <div className="rounded-xl bg-[#E8ECE9] p-3 text-xs text-[#1C2E24]">
              We will download a JSON file of <b>all your data</b> right now, and schedule your account for permanent deletion 30 days from today. You can cancel any time before then from this page.
            </div>
          )}
          <p className="text-sm text-[#5C6D63]">
            Type <b className="text-[#C76B58]">DELETE MY ACCOUNT</b> to confirm.
          </p>
          <Input value={phrase} onChange={(e) => setPhrase(e.target.value)}
                 placeholder="DELETE MY ACCOUNT" data-testid="delete-confirm-input"
                 className="rounded-xl bg-[#F9F8F6]" />
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={submit} disabled={busy} data-testid="confirm-delete-btn"
                    className="rounded-xl bg-[#C76B58] hover:bg-[#A85546]">
              {busy ? "Processing…" : (mode === "export_and_forget" ? "Download & schedule" : "Confirm deletion")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function GrievanceCard({ items, onSubmitted, dpoEmail }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "data", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Please fill subject and message"); return; }
    setBusy(true);
    try {
      await api.post("/privacy/grievances", form);
      toast.success("Grievance submitted. Our DPO will respond.");
      setOpen(false); setForm({ category: "data", subject: "", message: "" });
      onSubmitted();
    } catch { toast.error("Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-2xl border border-[#E5E7E4]" data-testid="grievance-card">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#C76B58]/15 flex items-center justify-center flex-shrink-0">
            <MessageSquareWarning className="w-5 h-5 text-[#C76B58]" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold">Submit a grievance</h3>
            <p className="text-sm text-[#5C6D63] mt-1">
              Raise concerns about your data, consents or access. Our DPO ({dpoEmail}) responds within 30 days.
            </p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="open-grievance-btn"
                className="rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">Submit grievance</Button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-[#7D8F84] italic">No grievances submitted yet.</p>
      ) : (
        <ul className="divide-y divide-[#E5E7E4]">
          {items.map(g => (
            <li key={g.id} className="py-3" data-testid={`grievance-${g.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium">{g.subject}</p>
                <StatusChip status={g.status} />
                <span className="text-xs text-[#7D8F84]">· {g.category}</span>
                <span className="text-xs text-[#7D8F84] ml-auto">{fmtDate(g.created_at)}</span>
              </div>
              <p className="text-sm text-[#5C6D63] mt-1">{g.message}</p>
              {g.response && (
                <div className="mt-2 p-3 rounded-xl bg-[#E8ECE9] text-sm">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4A6750]">DPO response · {fmtDate(g.resolved_at)}</p>
                  <p className="mt-1">{g.response}</p>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle>New grievance</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3" data-testid="grievance-form">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="grievance-category" className="rounded-xl bg-[#F9F8F6]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="consent">Consent</SelectItem>
                  <SelectItem value="data">Data accuracy / access</SelectItem>
                  <SelectItem value="access">Account access</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="gsub">Subject</Label>
              <Input id="gsub" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
                     placeholder="Briefly describe…" data-testid="grievance-subject"
                     className="mt-1.5 rounded-xl bg-[#F9F8F6]" />
            </div>
            <div>
              <Label htmlFor="gmsg">Message</Label>
              <Textarea id="gmsg" rows={4} value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        placeholder="Describe the issue or request…" data-testid="grievance-message"
                        className="mt-1.5 rounded-xl bg-[#F9F8F6]" />
            </div>
            <Button type="submit" disabled={busy} data-testid="submit-grievance-btn"
                    className="w-full rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
              {busy ? "Submitting…" : "Submit"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function StatusChip({ status }) {
  const m = {
    open: ["#7D8F84", "Open"],
    in_progress: ["#5B7582", "In progress"],
    resolved: ["#5B8266", "Resolved"],
    rejected: ["#C76B58", "Rejected"],
  };
  const [c, l] = m[status] || ["#7D8F84", status];
  return (
    <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
          style={{ color: c, background: `${c}15` }}>{l}</span>
  );
}

function RetentionCard({ policy }) {
  return (
    <Card className="p-6 rounded-2xl border border-[#E5E7E4]" data-testid="retention-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#4A6750]/15 flex items-center justify-center flex-shrink-0">
          <Clock className="w-5 h-5 text-[#4A6750]" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold">Data retention</h3>
          <p className="text-sm text-[#5C6D63] mt-1">How long we keep different categories of data.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {Object.entries(policy || {}).map(([k, v]) => (
          <div key={k} className="p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E7E4]">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#7D8F84]">{k.replace(/_/g, " ")}</p>
            <p className="text-sm text-[#1C2E24] mt-0.5">{v}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AdminMfaCard() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/mfa/status").catch(() => null);
    if (r) setStatus(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fetchQr = async () => {
    try {
      const r = await fetch(`${API}/admin/mfa/qr`, { credentials: "include" });
      if (!r.ok) return;
      const blob = await r.blob();
      setQrUrl(URL.createObjectURL(blob));
    } catch (e) {
      // QR is optional — the secret is still shown for manual entry
      toast.message("Could not load QR (network). Use the secret shown below to add manually.");
    }
  };

  const beginSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/mfa/setup");
      setSetup(r.data);
      await fetchQr();
    } catch (e) { toast.error(e?.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      await api.post("/admin/mfa/enable", { code });
      toast.success("Admin MFA enabled");
      setSetup(null); setCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!code || code.length < 6) { toast.error("Enter your current 6-digit code"); return; }
    setBusy(true);
    try {
      await api.post("/admin/mfa/disable", { code });
      toast.success("Admin MFA disabled");
      setCode(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); }
    finally { setBusy(false); }
  };

  if (!status) return null;

  return (
    <Card className="p-6 rounded-2xl border-2 border-[#1C2E24] bg-[#1C2E24]/[0.02]" data-testid="admin-mfa-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#1C2E24] flex items-center justify-center flex-shrink-0">
          <KeyRound className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-heading text-xl font-semibold">Admin two-factor (TOTP)</h3>
          <p className="text-sm text-[#5C6D63] mt-1">
            Required for admin accounts. Scan with Google Authenticator / 1Password / Authy.
          </p>
        </div>
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded"
              style={{ background: status.enabled ? "#5B826615" : "#C76B5815", color: status.enabled ? "#5B8266" : "#C76B58" }}>
          {status.enabled ? "Enabled" : "Disabled"}
        </span>
      </div>

      {!status.enabled && !setup && (
        <Button onClick={beginSetup} disabled={busy} data-testid="mfa-setup-btn"
                className="rounded-xl bg-[#1C2E24] hover:bg-[#0F1A14]">
          <Lock className="w-4 h-4 mr-2" /> Set up authenticator
        </Button>
      )}

      {setup && !status.enabled && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 items-center bg-white p-4 rounded-xl border border-[#E5E7E4]">
            <img alt="QR" src={qrUrl || ""} className="w-40 h-40 rounded-lg border border-[#E5E7E4]" />
            <div>
              <p className="text-sm text-[#5C6D63]">Scan this QR with your authenticator app, or enter the secret manually:</p>
              <p className="font-mono text-xs mt-2 break-all bg-[#F9F8F6] p-2 rounded select-all" data-testid="mfa-secret">{setup.secret}</p>
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Label>Enter 6-digit code from app</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                     placeholder="123456" inputMode="numeric" data-testid="mfa-verify-input"
                     className="mt-1.5 rounded-xl bg-[#F9F8F6] tracking-[0.3em] text-center" />
            </div>
            <Button onClick={enable} disabled={busy || code.length !== 6} data-testid="mfa-enable-btn"
                    className="h-10 rounded-xl bg-[#4A6750] hover:bg-[#3B5340]">
              {busy ? "Enabling…" : "Enable MFA"}
            </Button>
          </div>
        </div>
      )}

      {status.enabled && (
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Enter current 6-digit code to disable</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   placeholder="123456" inputMode="numeric" data-testid="mfa-disable-input"
                   className="mt-1.5 rounded-xl bg-[#F9F8F6] tracking-[0.3em] text-center" />
          </div>
          <Button onClick={disable} disabled={busy} variant="outline" data-testid="mfa-disable-btn"
                  className="h-10 rounded-xl border-[#C76B58] text-[#C76B58] hover:bg-[#C76B58]/5">
            Disable MFA
          </Button>
        </div>
      )}
    </Card>
  );
}
